// Google Analytics 4 + Microsoft Clarity, both gated by the "analytics" toggle
// in CookieConsent.tsx -- neither script is ever injected until the visitor
// has actually opted in, so there's nothing to disable at build time for
// environments without an ID configured (loadX() just no-ops).

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    clarity: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const CLARITY_PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID;

let gaLoaded = false;
let clarityLoaded = false;

const loadGoogleAnalytics = (): void => {
  if (gaLoaded || !GA_MEASUREMENT_ID) return;
  gaLoaded = true;

  window.dataLayer = window.dataLayer || [];
  // `arguments` y no (...args), aunque el linter prefiera lo segundo: gtag.js
  // reconoce sus propios comandos dentro de dataLayer porque llegan como el
  // objeto `arguments` (array-like, pero Array.isArray() da false). Un Array
  // de verdad no lo reconoce como comando, así que el `config` de abajo nunca
  // llega a aplicarse y no se envía un solo hit -- y el fallo es mudo: el
  // script carga, la consola queda limpia y Tiempo real vacío para siempre.
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  window.gtag('js', new Date());
  // We only reach this point after consent was already granted, so GA's own
  // Consent Mode starts "granted" here -- updateAnalyticsConsent() below is
  // what flips it back to "denied" if the visitor later changes their mind.
  window.gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied' });
  // send_page_view is off because Analytics.tsx sends page_view itself on
  // every route change (react-router doesn't reload the page, so gtag's own
  // automatic pageview-on-load would only ever fire once per session).
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false, anonymize_ip: true });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

const loadClarity = (): void => {
  if (clarityLoaded || !CLARITY_PROJECT_ID) return;
  clarityLoaded = true;

  // Microsoft's own snippet queues calls made before the real script
  // finishes loading via a `.q` array. We append to <head> directly rather
  // than their "insert before the first existing <script>" trick, since
  // that assumes a <script> tag is already present in the document.
  type ClarityFn = { (...args: unknown[]): void; q?: unknown[] };
  const w = window as unknown as Record<'clarity', ClarityFn>;
  w.clarity =
    w.clarity ||
    function (...args: unknown[]) {
      (w.clarity.q = w.clarity.q || []).push(args);
    };

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.clarity.ms/tag/' + CLARITY_PROJECT_ID;
  document.head.appendChild(script);
};

/** Called once on app boot with whatever consent is already on file (if any). */
export const initAnalyticsIfConsented = (analyticsConsent: boolean): void => {
  if (!analyticsConsent) return;
  loadGoogleAnalytics();
  loadClarity();
};

/** Called every time the visitor saves a consent choice, including changes. */
export const updateAnalyticsConsent = (analyticsConsent: boolean): void => {
  if (analyticsConsent) {
    loadGoogleAnalytics();
    loadClarity();
    if (window.gtag) window.gtag('consent', 'update', { analytics_storage: 'granted' });
    if (window.clarity) window.clarity('consent', true);
    return;
  }

  // Both scripts may already be loaded from earlier in the session -- tell
  // them to stop rather than leaving stale trackers running until reload.
  if (window.gtag) window.gtag('consent', 'update', { analytics_storage: 'denied' });
  if (window.clarity) window.clarity('consent', false);
};

export const trackPageView = (path: string): void => {
  if (gaLoaded && window.gtag) {
    window.gtag('event', 'page_view', { page_path: path });
  }
};

export const trackEvent = (name: string, params?: Record<string, unknown>): void => {
  if (gaLoaded && window.gtag) {
    window.gtag('event', name, params);
  }
  if (clarityLoaded && window.clarity) {
    window.clarity('event', name);
  }
};
